import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useEffect, useMemo, useState } from "react";
import { getDomain } from "@shared/blocks/helpers";
import { getLinkTitle, getLinkDescription } from "../helpers";
import "./view.scss";
const MAX_CLIENT_GIST_FILE_CHARS = 100_000;
const MAX_CLIENT_GIST_TOTAL_CHARS = 400_000;
const GIST_FETCH_TIMEOUT_MS = 12_000;
const GIST_PATH_SEGMENT_SKIP = new Set(["revisions", "edit", "raw", "forks", "comments"]);
const HYDRATION_CACHE = new Map();
const KEYWORDS_BY_LANGUAGE = {
    javascript: [
        "const",
        "let",
        "var",
        "function",
        "return",
        "if",
        "else",
        "for",
        "while",
        "switch",
        "case",
        "break",
        "continue",
        "class",
        "new",
        "import",
        "from",
        "export",
        "default",
        "async",
        "await",
        "try",
        "catch",
        "finally",
        "throw",
    ],
    typescript: [
        "const",
        "let",
        "var",
        "function",
        "return",
        "if",
        "else",
        "for",
        "while",
        "switch",
        "case",
        "break",
        "continue",
        "class",
        "new",
        "import",
        "from",
        "export",
        "default",
        "async",
        "await",
        "try",
        "catch",
        "finally",
        "throw",
        "type",
        "interface",
        "extends",
        "implements",
        "readonly",
        "public",
        "private",
        "protected",
    ],
    python: [
        "def",
        "class",
        "return",
        "if",
        "elif",
        "else",
        "for",
        "while",
        "in",
        "import",
        "from",
        "as",
        "pass",
        "break",
        "continue",
        "try",
        "except",
        "finally",
        "raise",
        "with",
        "lambda",
        "yield",
        "True",
        "False",
        "None",
    ],
    json: [],
    markdown: [],
};
const SUPPORTED_LANGUAGES = new Set(Object.keys(KEYWORDS_BY_LANGUAGE));
export const GistBlockView = memo(({ block }) => {
    const content = block.content;
    const url = content.url;
    const persistedGist = content.subtype === "gist" ? content.gist : undefined;
    const [fetchedGist, setFetchedGist] = useState();
    const gist = persistedGist ?? fetchedGist;
    const files = gist?.files ?? [];
    const [activeIndex, setActiveIndex] = useState(0);
    const gistIdFromUrl = useMemo(() => parseGistIdFromUrl(url), [url]);
    const fileTargetFromUrl = useMemo(() => parseGitHubFileTarget(url), [url]);
    // DISABLED: hydration fetch is commented out below, so isHydratingFromApi
    // is forced false. Otherwise unpersisted gist blocks would render a
    // forever-spinning skeleton. Restore the original expression below when
    // re-enabling the fetch.
    const isHydratingFromApi = false;
    // const isHydratingFromApi =
    //   (persistedGist?.files?.length ?? 0) === 0 &&
    //   fetchedGist === undefined &&
    //   (!!gistIdFromUrl || !!fileTargetFromUrl) &&
    //   !!url;
    useEffect(() => {
        setActiveIndex(0);
    }, [gist?.gistId]);
    // DISABLED NETWORK FETCH: this useEffect was calling api.github.com/gists/*
    // and raw.githubusercontent.com on mount to hydrate gist contents that
    // weren't persisted on the block. Disabled to keep initial load snappy
    // and avoid burning GitHub's 60 req/hr unauthenticated rate limit.
    // Re-enable by restoring the block below; better long-term fix is to
    // hydrate at ingestion time or lazy-load on viewport entry.
    //
    // useEffect(() => {
    //   if (persistedGist?.files && persistedGist.files.length > 0) {
    //     return;
    //   }
    //   if (!url || (!gistIdFromUrl && !fileTargetFromUrl)) {
    //     return;
    //   }
    //
    //   let cancelled = false;
    //
    //   const hydrate = async (): Promise<void> => {
    //     try {
    //       if (fileTargetFromUrl) {
    //         const filePayload = await fetchGitHubFilePayload(fileTargetFromUrl);
    //         if (!filePayload || cancelled) return;
    //         setFetchedGist(filePayload);
    //         return;
    //       }
    //       if (!gistIdFromUrl) return;
    //       const gistPayload = await fetchGistPayload(gistIdFromUrl);
    //       if (!gistPayload || cancelled) return;
    //       setFetchedGist(gistPayload);
    //     } catch {
    //       // best-effort hydration only
    //     }
    //   };
    //
    //   void hydrate();
    //
    //   return () => {
    //     cancelled = true;
    //   };
    // }, [persistedGist?.files, gistIdFromUrl, fileTargetFromUrl, url]);
    const safeIndex = files.length > 0 ? Math.min(activeIndex, files.length - 1) : 0;
    const activeFile = files[safeIndex];
    const activeLanguage = normalizeLanguage(activeFile?.language);
    const activeLines = activeFile ? activeFile.content.split("\n") : [];
    if (files.length === 0) {
        if (isHydratingFromApi) {
            return (_jsx("div", { className: "gist-content gist-content--loading", "aria-busy": "true", "aria-label": "Loading gist preview", children: _jsxs("div", { className: "gist-content__code-panel", children: [_jsxs("div", { className: "gist-content__code-toolbar", children: [_jsx("div", { className: "gist-content__skeleton gist-content__skeleton--tab" }), _jsx("span", { className: "gist-content__line-count", children: "..." })] }), _jsx("pre", { className: "gist-content__pre", children: _jsx("code", { className: "gist-content__code", children: Array.from({ length: 8 }).map((_, idx) => (_jsxs("span", { className: "gist-content__line", children: [_jsx("span", { className: "gist-content__line-number", children: idx + 1 }), _jsx("span", { className: "gist-content__line-body", children: _jsx("span", { className: "gist-content__skeleton gist-content__skeleton--line" }) })] }, `loading-${idx}`))) }) })] }) }));
        }
        const domain = getDomain(url) ?? "gist.github.com";
        const pageTitle = getLinkTitle(content) || persistedGist?.gistDescription || block.title || "Gist";
        const fallbackDescription = getLinkDescription(content) || block.summary || "";
        return (_jsxs("div", { className: "gist-content gist-content--fallback", children: [pageTitle && _jsx("div", { className: "gist-content__title", children: pageTitle }), _jsx("div", { className: "gist-content__source", children: domain }), fallbackDescription && (_jsx("div", { className: "gist-content__description", children: fallbackDescription }))] }));
    }
    return (_jsx("div", { className: "gist-content", children: _jsxs("div", { className: "gist-content__code-panel", children: [_jsxs("div", { className: "gist-content__code-toolbar", children: [files.length > 1 ? (_jsx("div", { className: "gist-content__tabs", role: "tablist", "aria-label": "Gist files", children: files.map((f, i) => (_jsx("button", { type: "button", role: "tab", "aria-selected": i === safeIndex, className: `gist-content__tab${i === safeIndex ? " gist-content__tab--active" : ""}`, onClick: (e) => {
                                    e.stopPropagation();
                                    setActiveIndex(i);
                                }, onMouseDown: (e) => e.stopPropagation(), children: f.filename }, f.filename))) })) : activeFile ? (_jsx("div", { className: "gist-content__file-label", children: _jsx("span", { className: "gist-content__filename", children: activeFile.filename }) })) : null, activeFile ? (_jsx("span", { className: "gist-content__line-count", "aria-label": `${activeLines.length} lines`, children: activeLines.length })) : null] }), activeFile ? (_jsx("pre", { className: "gist-content__pre", children: _jsx("code", { className: "gist-content__code", children: activeLines.map((line, idx) => (_jsxs("span", { className: "gist-content__line", children: [_jsx("span", { className: "gist-content__line-number", children: idx + 1 }), _jsx("span", { className: "gist-content__line-body", children: tokenizeLine(line, activeLanguage).map((token, tokenIdx) => (_jsx("span", { className: token.tokenClass
                                            ? `gist-content__token gist-content__token--${token.tokenClass}`
                                            : undefined, children: token.text.length > 0 ? token.text : " " }, `${idx}-${tokenIdx}-${token.text.length}`))) })] }, `${idx}-${line.length}`))) }) })) : null] }) }));
});
function normalizeLanguage(language) {
    if (!language)
        return "text";
    const normalized = language.toLowerCase();
    if (normalized === "js")
        return "javascript";
    if (normalized === "ts")
        return "typescript";
    if (normalized === "py")
        return "python";
    if (normalized === "md")
        return "markdown";
    return normalized;
}
function parseGistIdFromUrl(url) {
    if (!url)
        return undefined;
    try {
        const parsed = new URL(url);
        if (parsed.hostname.toLowerCase() !== "gist.github.com") {
            return undefined;
        }
        const parts = parsed.pathname.split("/").filter(Boolean);
        if (parts.length === 0)
            return undefined;
        let i = parts.length - 1;
        while (i >= 0 && GIST_PATH_SEGMENT_SKIP.has(parts[i])) {
            i--;
        }
        return i >= 0 ? parts[i] : undefined;
    }
    catch {
        return undefined;
    }
}
function parseGitHubFileTarget(url) {
    if (!url)
        return undefined;
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.toLowerCase();
        const parts = parsed.pathname.split("/").filter(Boolean);
        if (host === "raw.githubusercontent.com") {
            if (parts.length < 4)
                return undefined;
            const [owner, repo, ref, ...fileParts] = parts;
            if (!owner || !repo || !ref || fileParts.length === 0)
                return undefined;
            const filename = decodeURIComponent(fileParts[fileParts.length - 1] ?? "");
            if (!filename)
                return undefined;
            return {
                fileId: `${owner}/${repo}/${ref}/${fileParts.join("/")}`,
                filename,
                rawUrl: parsed.toString(),
                language: inferLanguageFromFilename(filename),
            };
        }
        if (host === "github.com" || host === "www.github.com") {
            if (parts.length < 5 || parts[2] !== "blob")
                return undefined;
            const [owner, repo, , ref, ...fileParts] = parts;
            if (!owner || !repo || !ref || fileParts.length === 0)
                return undefined;
            const filename = decodeURIComponent(fileParts[fileParts.length - 1] ?? "");
            if (!filename)
                return undefined;
            const encodedPath = fileParts.map((part) => encodeURIComponent(part)).join("/");
            return {
                fileId: `${owner}/${repo}/${ref}/${fileParts.join("/")}`,
                filename,
                rawUrl: `https://raw.githubusercontent.com/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/${encodeURIComponent(ref)}/${encodedPath}`,
                language: inferLanguageFromFilename(filename),
            };
        }
        return undefined;
    }
    catch {
        return undefined;
    }
}
async function fetchGistPayload(gistIdFromUrl) {
    const cacheKey = `gist:${gistIdFromUrl}`;
    const cached = HYDRATION_CACHE.get(cacheKey);
    if (cached)
        return cached;
    const response = await fetchWithTimeout(`https://api.github.com/gists/${encodeURIComponent(gistIdFromUrl)}`, {
        headers: {
            Accept: "application/vnd.github+json",
            "X-GitHub-Api-Version": "2022-11-28",
        },
    });
    if (!response.ok) {
        return undefined;
    }
    const data = (await response.json());
    const filesRaw = data.files ? Object.values(data.files) : [];
    const filesHydrated = [];
    let total = 0;
    for (const f of filesRaw) {
        if (typeof f.filename !== "string")
            continue;
        let body = typeof f.content === "string" ? f.content : "";
        if ((body.length === 0 || f.truncated === true) && typeof f.raw_url === "string") {
            try {
                const rawRes = await fetchWithTimeout(f.raw_url, { headers: { Accept: "text/plain,*/*" } });
                if (rawRes.ok) {
                    body = await rawRes.text();
                }
            }
            catch {
                // keep best effort behavior
            }
        }
        if (body.length === 0)
            continue;
        if (body.length > MAX_CLIENT_GIST_FILE_CHARS) {
            body = body.slice(0, MAX_CLIENT_GIST_FILE_CHARS);
        }
        if (total + body.length > MAX_CLIENT_GIST_TOTAL_CHARS) {
            break;
        }
        total += body.length;
        filesHydrated.push({
            filename: f.filename,
            language: f.language ?? undefined,
            content: body,
        });
    }
    if (filesHydrated.length === 0)
        return undefined;
    const payload = {
        gistId: data.id ?? gistIdFromUrl,
        description: data.description ?? undefined,
        files: filesHydrated,
    };
    HYDRATION_CACHE.set(cacheKey, payload);
    return payload;
}
async function fetchGitHubFilePayload(target) {
    const cacheKey = `file:${target.rawUrl}`;
    const cached = HYDRATION_CACHE.get(cacheKey);
    if (cached)
        return cached;
    const response = await fetchWithTimeout(target.rawUrl, { headers: { Accept: "text/plain,*/*" } });
    if (!response.ok)
        return undefined;
    const body = await response.text();
    if (!body)
        return undefined;
    const content = body.length > MAX_CLIENT_GIST_FILE_CHARS
        ? body.slice(0, MAX_CLIENT_GIST_FILE_CHARS)
        : body;
    const payload = {
        gistId: target.fileId,
        files: [
            {
                filename: target.filename,
                language: target.language,
                content,
            },
        ],
    };
    HYDRATION_CACHE.set(cacheKey, payload);
    return payload;
}
async function fetchWithTimeout(input, init) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GIST_FETCH_TIMEOUT_MS);
    try {
        return await fetch(input, {
            ...init,
            signal: controller.signal,
        });
    }
    finally {
        clearTimeout(timeoutId);
    }
}
function inferLanguageFromFilename(filename) {
    const match = /\.([A-Za-z0-9]+)$/.exec(filename);
    if (!match)
        return undefined;
    const ext = match[1]?.toLowerCase();
    if (!ext)
        return undefined;
    if (ext === "js" || ext === "jsx")
        return "javascript";
    if (ext === "ts" || ext === "tsx")
        return "typescript";
    if (ext === "py")
        return "python";
    if (ext === "md" || ext === "mdx")
        return "markdown";
    if (ext === "json")
        return "json";
    return ext;
}
function tokenizeLine(line, language) {
    if (!line) {
        return [{ text: " " }];
    }
    if (language === "markdown") {
        if (/^\s*#{1,6}\s/.test(line)) {
            return [{ text: line, tokenClass: "markdown" }];
        }
        return splitWithPatterns(line, [
            { regex: /`[^`]+`/g, tokenClass: "string" },
            { regex: /\*\*[^*]+\*\*/g, tokenClass: "keyword" },
            { regex: /\[[^\]]+\]\([^)]+\)/g, tokenClass: "function" },
        ]);
    }
    if (language === "json") {
        return splitWithPatterns(line, [
            { regex: /"(?:\\.|[^"\\])*"\s*:/g, tokenClass: "keyword" },
            { regex: /"(?:\\.|[^"\\])*"/g, tokenClass: "string" },
            { regex: /\b(true|false|null)\b/g, tokenClass: "keyword" },
            { regex: /\b-?\d+(?:\.\d+)?\b/g, tokenClass: "number" },
        ]);
    }
    if (language === "python") {
        return splitWithPatterns(line, [
            { regex: /#.*$/g, tokenClass: "comment" },
            { regex: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g, tokenClass: "string" },
            { regex: /\b\d+(?:\.\d+)?\b/g, tokenClass: "number" },
            { regex: /\b[A-Za-z_]\w*(?=\s*\()/g, tokenClass: "function" },
            { regex: keywordRegex(language), tokenClass: "keyword" },
            { regex: /[=+\-*/<>!]+/g, tokenClass: "operator" },
        ]);
    }
    if (SUPPORTED_LANGUAGES.has(language)) {
        return splitWithPatterns(line, [
            { regex: /\/\/.*$/g, tokenClass: "comment" },
            { regex: /\/\*[\s\S]*?\*\//g, tokenClass: "comment" },
            { regex: /`(?:\\.|[^`])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g, tokenClass: "string" },
            { regex: /\b\d+(?:\.\d+)?\b/g, tokenClass: "number" },
            { regex: /\b[A-Za-z_]\w*(?=\s*\()/g, tokenClass: "function" },
            { regex: keywordRegex(language), tokenClass: "keyword" },
            { regex: /[=+\-*/<>!]+/g, tokenClass: "operator" },
        ]);
    }
    return [{ text: line }];
}
function keywordRegex(language) {
    const keywords = KEYWORDS_BY_LANGUAGE[language] ?? [];
    if (keywords.length === 0) {
        return /\b\B/;
    }
    return new RegExp(`\\b(?:${keywords.join("|")})\\b`, "g");
}
function splitWithPatterns(line, patterns) {
    const marks = [];
    for (const pattern of patterns) {
        const regex = new RegExp(pattern.regex.source, pattern.regex.flags.includes("g") ? pattern.regex.flags : `${pattern.regex.flags}g`);
        let match = regex.exec(line);
        while (match) {
            if (match[0].length > 0) {
                marks.push({
                    start: match.index,
                    end: match.index + match[0].length,
                    tokenClass: pattern.tokenClass,
                });
            }
            match = regex.exec(line);
        }
    }
    marks.sort((a, b) => {
        if (a.start !== b.start)
            return a.start - b.start;
        return b.end - a.end;
    });
    const accepted = [];
    for (const mark of marks) {
        const overlaps = accepted.some((other) => !(mark.end <= other.start || mark.start >= other.end));
        if (overlaps === false) {
            accepted.push(mark);
        }
    }
    accepted.sort((a, b) => a.start - b.start);
    const tokens = [];
    let cursor = 0;
    for (const mark of accepted) {
        if (mark.start > cursor) {
            tokens.push({ text: line.slice(cursor, mark.start) });
        }
        tokens.push({
            text: line.slice(mark.start, mark.end),
            tokenClass: mark.tokenClass,
        });
        cursor = mark.end;
    }
    if (cursor < line.length) {
        tokens.push({ text: line.slice(cursor) });
    }
    return tokens.length > 0 ? tokens : [{ text: line }];
}
